import dayjs from 'dayjs';
import {makeAutoObservable, runInAction, toJS} from 'mobx';

import {pollAsync} from '@yourcoach/async-utils';
import {i18n} from '@yourcoach/i18n';

import type {Nullable} from '../../../../../utils/utility-types';
import type {AttachFileConfig} from '../../../editor/models/Editor/types';
import type {AttachmentApiService} from '../../../services/api/attachmentService/AttachmentApi.service';
import type {ApiFileModel} from '../../../services/api/models/ApiFile/ApiFile.model';
import type {WSService} from '../../../services/ws/wsService/WS.service';
import type {BaseAttachmentModel} from '../BaseAttachment';

import {logger} from '../../../../../utils/logger';
import {FlagManager} from '../../../../../utils/store';
import {isDefined, isNullish} from '../../../../../utils/typecheck';
import {AttachmentType} from '../utils';
import {WSFileAttachmentManager} from './WsFileAttachmentManager/WSFileAttachmentManager';

const VOICE_TRANSCRIPTIONS_SUPPORT_FROM_DATE = dayjs('2024-12-03');

const TRANSCRIPT_POLL_TIMEOUT_MS = 60_000;

export const isVoiceTranscriptSupportedByDate = (
  createdDate: ApiFileModel['createdDate'],
) => !dayjs(createdDate).isBefore(VOICE_TRANSCRIPTIONS_SUPPORT_FROM_DATE);

const isVoiceTranscriptionMissing = (transcript: Nullable<string>) =>
  isNullish(transcript);

export class FileAttachmentModel implements BaseAttachmentModel<ApiFileModel> {
  fileConfig: AttachFileConfig | undefined;
  public wsManager = new WSFileAttachmentManager(this);
  private isUploading = new FlagManager(false);
  private uploadingError: Nullable<Error> = null;

  constructor(
    public apiModel: ApiFileModel,
    public readonly __injections: {
      attachmentApiService: AttachmentApiService;
      wsService: WSService;
    },
  ) {
    makeAutoObservable<FileAttachmentModel, 'uploadingError'>(
      this,
      {
        __injections: false,
        uploadingError: false,
      },
      {
        autoBind: true,
      },
    );

    this.processWsRegister();
  }

  destruct() {}

  get attachmentType() {
    return this.apiModel.attachmentType;
  }

  get createdDate() {
    return this.apiModel.createdDate;
  }

  get description() {
    return this.apiModel.description;
  }

  get fileName() {
    return this.apiModel.fileName;
  }

  get fileSize() {
    return this.src.size;
  }

  get fileSizeString() {
    if (this.fileSize) {
      return i18n.fileSize(this.fileSize);
    }

    return this.src.url ?? '';
  }

  get id() {
    return this.apiModel.id;
  }

  get imageSize() {
    return this.apiModel.previewSrc?.imageSize ?? {height: 0, width: 0};
  }

  get isMy() {
    return this.apiModel.isMy;
  }

  get isPlayable() {
    if (this.attachmentType === AttachmentType.VOICE) {
      return isDefined(this.apiModel.playableSrc);
    }

    return true;
  }

  get isUploaded() {
    return Boolean(this.apiModel.isUploaded);
  }

  get isVerticalImage() {
    return this.imageSize.height > this.imageSize.width;
  }

  get mimetype() {
    return this.src.mime;
  }

  get originalSrc() {
    return this.src.url;
  }

  get previewSrc() {
    return this.apiModel.previewSrc?.url ?? null;
  }

  get shouldRegisterToTranscribe() {
    return (
      this.attachmentType === AttachmentType.VOICE &&
      isVoiceTranscriptSupportedByDate(this.createdDate) &&
      isVoiceTranscriptionMissing(this.transcript)
    );
  }

  get transcript() {
    return this.apiModel.transcript;
  }

  get usedByIds() {
    return this.apiModel.usedByIds;
  }

  async fetchTranscript() {
    await pollAsync(() => isDefined(this.apiModel.transcript), {
      timeout: TRANSCRIPT_POLL_TIMEOUT_MS,
      timeoutMessage: i18n.t('shared:this_audio_does_not_have_transcription'),
    });
  }

  updateApiModel(model: ApiFileModel) {
    const {originalSrc, previewSrc} = this.cacheSrc(model);

    model.update({originalSrc, previewSrc});

    this.apiModel.update(model);

    return this;
  }

  async upload(config: AttachFileConfig) {
    this.uploadingError = null;

    if (this.isUploaded) {
      return;
    }

    this.fileConfig = config;

    runInAction(() => {
      this.isUploading.setTrue();
    });

    try {
      const response =
        await this.attachmentApiService.uploadFileAttachment(config);

      this.updateApiModel(response);

      this.wsManager.registerToConvert();
      this.wsManager.registerToTranscribe();
    } catch (e) {
      logger.error(e);

      this.uploadingError = e;

      throw e;
    } finally {
      runInAction(() => {
        this.isUploading.setFalse();
      });
    }
  }

  async uploadIfNeeded() {
    if (this.isUploaded) {
      return this;
    }

    if (this.isUploading.isTrue) {
      await pollAsync(() => this.isUploading.isFalse);

      if (!this.isUploaded) {
        if (this.uploadingError) {
          throw this.uploadingError;
        } else {
          throw new Error(
            'FileAttachmentModel: unable to upload file because of unknown error',
          );
        }
      }
    } else if (this.fileConfig) {
      // if the attachment hasn't been uploaded yet we should try to upload it again
      await this.upload(this.fileConfig);
    }

    return this;
  }

  private get attachmentApiService(): AttachmentApiService {
    return this.__injections.attachmentApiService;
  }

  private get src() {
    if (this.attachmentType === AttachmentType.VOICE) {
      return this.apiModel.playableSrc ?? this.apiModel.originalSrc;
    }

    return this.apiModel.originalSrc;
  }

  private cacheSrc(_model: ApiFileModel) {
    if (this.attachmentType === AttachmentType.IMAGE) {
      const {originalSrc, previewSrc} = toJS(this.apiModel);

      return {
        originalSrc,
        previewSrc,
      };
    }

    // TODO: we could cache playableSrc at lease for web is we had controlled uuid
    // const shouldCachePlayableSrc =
    //   this.attachmentType === AttachmentType.VOICE &&
    //   this.envProviderService.platform.isWeb &&
    //   this.apiModel.originalSrc.url?.includes('blob');
    //
    // if (shouldCachePlayableSrc) {
    //   const {originalSrc, playableSrc} = toJS(this.apiModel);
    //
    //   return {
    //     originalSrc,
    //     playableSrc,
    //   };
    // }

    return {};
  }

  private processWsRegister() {
    if (!this.isPlayable) {
      this.wsManager.registerToConvert();
    }

    if (this.shouldRegisterToTranscribe) {
      this.wsManager.registerToTranscribe();
    }
  }
}
